import React, { useContext } from "react";

import classNames from "classnames";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import { hoursTypeMap } from "../../helpers/vacancyHelpers";
import Loader from "../Loader/Loader";




const StyledImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${(props) =>
    props.showCloseDate || props.showHoursType ? "space-between" : "left"};
`;

const StyledLocationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  
  .location-text {
    color: #ffffff;
    font-size: 0.9rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const StyledJobTitle = styled.h3`
  color: #ffffff;
  font-weight: bold;
  font-size: 1.5rem;
  line-height: 1.3;
  margin-bottom: 16px;
  text-transform: uppercase;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const StyledDetailsRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  color: #ffffff;
  font-size: 0.9rem;
  margin-bottom: 8px;
  
  .detail-separator {
    margin: 0 4px;
    flex-shrink: 0;
  }
  
  .wrap-text {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  .remuneration-text {
    flex: 1;
    min-width: 0;
    word-break: normal;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
`;

const StyledVacancy = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: none; /* Remove border */
  color: inherit;
  text-decoration: none;
  background-color: #ffffff; /* Set the background color to white */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Add some shadow for depth */
  border-radius: 8px; /* Round the corners */
  overflow: hidden; /* Ensure the content fits within the rounded corners */
  text-align: left !important; /* Left-align text */
  padding: 16px; /* Add padding inside the card */
`;

function formatCategory(category) {
  if (category == null) {
    return "";
  }

  // Remove non-alphabetic characters and replace spaces with hyphens
  return category.replace(/[^a-zA-Z ]/g, "").replace(/ /g, "-");
}

function DescriptiveItem(props) {
  const { vacancy, isLoading, applyButton } = props;
  const { trackingStore, configStore } = useStores();
  const { config } = configStore;
  const {
    thumbnail,
    showCloseDate,
    additionalConfig,
    useComanyLogoAsThumbnail,
  } = useContext(ComponentContext);

  const vacancyHref = {
    pathname: `/job/${vacancy.id}`,
    query: trackingStore.refererQuery,
  };

  let thumbnailURL = thumbnail?.url || config?.logo?.url;

  if (useComanyLogoAsThumbnail && vacancy?.company?.logoUrlFull) {
    thumbnailURL = vacancy?.company?.logoUrlFull;
  }

  const styledVacancyClassName = classNames(
    "h-100 d-flex flex-column",
    {
      [formatCategory(vacancy?.category)]: vacancy?.category,
    }
  );

  let endDate = vacancy?.endDate;

  if (endDate) {
    const dateObject = new Date(endDate);

    // Checking if the date is valid
    if (!Number.isNaN(dateObject.getTime())) {
      endDate = dateObject.toLocaleDateString();
    } else {
      endDate = undefined;
    }
  }

  let openedSince = "";

  if (additionalConfig?.showTitleSinceOpened && vacancy?.validFrom) {
    // calculate the difference since the job was opened to today eg 2 days ago
    const today = new Date();
    const openedDate = new Date(vacancy?.validFrom);
    const diffTime = Math.abs(today - openedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    openedSince = `${diffDays}d`;

    if (diffDays === 1) {
      openedSince = `${diffDays}d`;
    }
  }

  return (
      <StyledVacancy
        $withoutImage={!thumbnailURL}
        className={styledVacancyClassName}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
             {thumbnailURL && (
              <StyledImageWrapper
                className="pt-2 pb-4 description-image-wrapper"
                showCloseDate={showCloseDate}
                showHoursType={additionalConfig?.showCardHoursTitle}
              >
                <Image
                  alt={vacancy?.jobTitle}
                  height={50}
                  layout="fixed"
                  objectFit="contain"
                  priority
                  src={thumbnailURL}
                  width={50}
                />
              </StyledImageWrapper>
            )}

            {/* 2. Location - Below Logo */}
            {!additionalConfig?.hideLocation && vacancy?.location?.city && (
              <StyledLocationRow>
                {/* Placeholder Location Icon SVG */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.72612 0.333008C3.46592 0.333008 0 3.79892 0 8.05909C0 13.3461 6.91412 21.1077 7.2085 21.4355C7.485 21.7435 7.96775 21.743 8.24375 21.4355C8.53812 21.1077 15.4522 13.3461 15.4522 8.05909C15.4522 3.79892 11.9863 0.333008 7.72612 0.333008ZM7.72612 11.9463C5.58271 11.9463 3.83896 10.2025 3.83896 8.05909C3.83896 5.91567 5.58275 4.17192 7.72612 4.17192C9.8695 4.17192 11.6132 5.91572 11.6132 8.05913C11.6132 10.2025 9.8695 11.9463 7.72612 11.9463Z"
                    fill="#ffffff"
                  />
                </svg>
                <span className="location-text">{vacancy.location.city}</span>
              </StyledLocationRow>
            )}

            {/* 3. Job Title - Main Body */}
            <StyledJobTitle>{vacancy?.jobTitle || vacancy?.title}</StyledJobTitle>

            {/* 4. Department • Days Ago - First Detail Row */}
            {!additionalConfig?.hideCardCategory && vacancy?.category && (
              <StyledDetailsRow>
                <span className="wrap-text">{vacancy.category}</span>
                {openedSince && (
                  <>
                    <span className="detail-separator">•</span>
                    {/* Placeholder Refresh/Time Icon SVG */}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M14 6.74667H9.48L11.3067 4.86667C9.48666 3.06667 6.54 3 4.72 4.8C2.9 6.60667 2.9 9.52 4.72 11.3267C6.54 13.1333 9.48666 13.1333 11.3067 11.3267C12.2133 10.4333 12.6667 9.38667 12.6667 8.06667H14C14 9.38667 13.4133 11.1 12.24 12.26C9.9 14.58 6.1 14.58 3.76 12.26C1.42666 9.94667 1.40666 6.18667 3.74666 3.87333C6.08666 1.56 9.84 1.56 12.18 3.87333L14 2V6.74667ZM8.33333 5.33333V8.16667L10.6667 9.55333L10.1867 10.36L7.33333 8.66667V5.33333H8.33333Z"
                        fill="#ffffff"
                      />
                    </svg>
                    <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                      {openedSince}
                    </span>
                  </>
                )}
              </StyledDetailsRow>
            )}

            {/* 5. Employment Type • Salary - Second Detail Row */}
            <StyledDetailsRow>
              {additionalConfig?.showCardHours && vacancy?.hoursType && (
                <>
                  {/* Placeholder Briefcase Icon SVG */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <path
                      d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4ZM20 19H4V8H20V19Z"
                      fill="#ffffff"
                    />
                  </svg>
                  <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                    {hoursTypeMap[vacancy.hoursType]}
                  </span>
                </>
              )}
              {!additionalConfig?.hideRemuneration &&
                vacancy?.remuneration?.description && (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M11.5 2C9.4233 2 7.39323 2.61581 5.66652 3.76957C3.9398 4.92332 2.59399 6.5632 1.79927 8.48182C1.00455 10.4004 0.796614 12.5116 1.20176 14.5484C1.6069 16.5852 2.60693 18.4562 4.07538 19.9246C5.54383 21.3931 7.41475 22.3931 9.45155 22.7982C11.4884 23.2034 13.5996 22.9954 15.5182 22.2007C17.4368 21.406 19.0767 20.0602 20.2304 18.3335C21.3842 16.6068 22 14.5767 22 12.5C22 11.1211 21.7284 9.75574 21.2007 8.48182C20.6731 7.2079 19.8996 6.05039 18.9246 5.07538C17.9496 4.10036 16.7921 3.32694 15.5182 2.79926C14.2443 2.27159 12.8789 2 11.5 2ZM14.3327 17.8102H8.23334C8.08907 17.8102 7.94834 17.7656 7.83039 17.6825C7.71244 17.5995 7.62303 17.482 7.5744 17.3461C7.52578 17.2103 7.5203 17.0628 7.55873 16.9237C7.59715 16.7847 7.6776 16.6609 7.78907 16.5693L10.2232 14.5697C10.0482 14.2985 9.82234 13.949 9.5904 13.5897H8.23334C8.04769 13.5897 7.86964 13.5159 7.73836 13.3846C7.60709 13.2534 7.53334 13.0753 7.53334 12.8897C7.53334 12.704 7.60709 12.526 7.73836 12.3947C7.86964 12.2634 8.04769 12.1897 8.23334 12.1897H8.7C8.602 12.0329 8.51567 11.8943 8.4606 11.8019C8.1773 11.3051 8.02596 10.7441 8.021 10.1723C8.02085 9.44844 8.26328 8.74545 8.70957 8.17559C9.15586 7.60572 9.78026 7.20187 10.483 7.02853C11.1858 6.85519 11.9264 6.92237 12.5864 7.21934C13.2465 7.51631 13.7881 8.02593 14.1245 8.6668C14.2112 8.8311 14.229 9.02309 14.1741 9.20053C14.1192 9.37797 13.996 9.52633 13.8317 9.61296C13.6674 9.6996 13.4754 9.71742 13.298 9.66251C13.1205 9.60759 12.9722 9.48443 12.8855 9.32013C12.6948 8.95619 12.3875 8.6667 12.0128 8.49793C11.6382 8.32917 11.2178 8.29088 10.8188 8.38917C10.4198 8.48747 10.0653 8.71668 9.81189 9.04017C9.5585 9.36366 9.42088 9.76275 9.421 10.1737C9.42557 10.4919 9.50792 10.8041 9.66087 11.0832C9.78547 11.2909 10.0547 11.716 10.3581 12.1911H12.8972C13.0829 12.1911 13.2609 12.2648 13.3922 12.3961C13.5235 12.5274 13.5972 12.7054 13.5972 12.8911C13.5972 13.0767 13.5235 13.2548 13.3922 13.386C13.2609 13.5173 13.0829 13.5911 12.8972 13.5911H11.2578C11.5331 14.0162 11.7338 14.3261 11.7399 14.3349C11.8336 14.4793 11.8709 14.6532 11.8445 14.8233C11.8182 14.9934 11.7301 15.1478 11.5971 15.2571L10.1901 16.413H14.3327C14.5183 16.413 14.6964 16.4867 14.8276 16.618C14.9589 16.7493 15.0327 16.9273 15.0327 17.113C15.0327 17.2986 14.9589 17.4767 14.8276 17.608C14.6964 17.7392 14.5183 17.813 14.3327 17.813V17.8102Z"
                        fill="#ffffff"
                      />
                    </svg>
                    <span className="remuneration-text">
                      {vacancy.remuneration.description}
                    </span>
                  </>
                )}
            </StyledDetailsRow>
            {applyButton}
          </>
        )}
      </StyledVacancy>
  );
}

export default observer(DescriptiveItem);

DescriptiveItem.propTypes = {
  isLoading: PropTypes.bool,
  vacancy: PropTypes.shape({
    category: PropTypes.string,
    company: PropTypes.shape({
      name: PropTypes.string,
    }),
    hoursType: PropTypes.string,
    id: PropTypes.number,
    jobTitle: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
    }),
    remuneration: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
};

DescriptiveItem.defaultProps = {
  isLoading: false,
  vacancy: {},
};
